// Admin Users Section - User statistics dashboard
// Sub-admin page for viewing user statistics and analytics

import React, { useState, useEffect } from 'react';
import { MainLayout } from '../../../../components/Layout';
import { LoadingPanel, RevoltButton, DropdownSelect, RevoltSwitch } from '../../../../components';
import { AdminPageHeader } from '../../components';
import { AdminUsersManager } from './managers/admin-users-manager';
import { useManager } from '../../../../core';
import { logger } from '../../../../core';
import { NotificationModal, SignupsOverTimeChart } from './components';
import './AdminUsersSection.css';

function startOfUtcDay(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function addUtcDays(date, days) {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
}

function toUtcDateOnlyString(date) {
  const yyyy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getRangeFromPreset(preset, utcEndDate) {
  const endStr = toUtcDateOnlyString(utcEndDate);

  if (preset === 'last_day') {
    const start = utcEndDate;
    return { signupsStart: toUtcDateOnlyString(start), signupsEnd: endStr };
  }

  if (preset === 'last_week') {
    const start = addUtcDays(utcEndDate, -6);
    return { signupsStart: toUtcDateOnlyString(start), signupsEnd: endStr };
  }

  if (preset === 'last_month') {
    const start = addUtcDays(utcEndDate, -29);
    return { signupsStart: toUtcDateOnlyString(start), signupsEnd: endStr };
  }

  if (preset === 'month_to_date') {
    const start = new Date(Date.UTC(utcEndDate.getUTCFullYear(), utcEndDate.getUTCMonth(), 1));
    return { signupsStart: toUtcDateOnlyString(start), signupsEnd: endStr };
  }

  if (preset === 'year_to_date') {
    const start = new Date(Date.UTC(utcEndDate.getUTCFullYear(), 0, 1));
    return { signupsStart: toUtcDateOnlyString(start), signupsEnd: endStr };
  }

  if (preset === 'revolt_start') {
    const start = new Date(Date.UTC(2026, 1, 16)); // 2026-02-16 UTC
    return { signupsStart: toUtcDateOnlyString(start), signupsEnd: endStr };
  }

  // custom is handled in component state
  return { signupsStart: endStr, signupsEnd: endStr };
}

function StatCard({ title, value, subtitle, icon, color = '#8b0000' }) {
  const displayValue = typeof value === 'number' ? value.toLocaleString() : '—';

  return (
    <div className="admin-stat-card">
      <div className="admin-stat-icon" style={{ color }}>
        <i className={`fa-solid fa-${icon}`}></i>
      </div>
      <div className="admin-stat-content">
        <h3 className="admin-stat-value">{displayValue}</h3>
        <h4 className="admin-stat-title">{title}</h4>
        {subtitle && <p className="admin-stat-subtitle">{subtitle}</p>}
      </div>
    </div>
  );
}

const PLAN_COLORS = { free: '#6b7280', pro: '#3b82f6', max: '#8b0000' };
const PLAN_ORDER = ['free', 'pro', 'max']; /* Grid: top-left=free, top-right=pro, bottom-left=max */
const TIER_ORDER = ['enhanced', 'basic'];

function getTierOrder(tiers) {
  const keys = Object.keys(tiers || {});
  return [...TIER_ORDER.filter(t => keys.includes(t)), ...keys.filter(t => !TIER_ORDER.includes(t))];
}

function PlanTierDistribution({ planTierDistribution }) {
  if (!planTierDistribution || typeof planTierDistribution !== 'object') {
    return (
      <div className="admin-info-card">
        <h4>Plan & Tier Distribution</h4>
        <p className="admin-plan-tier-empty">No data available.</p>
      </div>
    );
  }

  const plans = [...PLAN_ORDER.filter(p => planTierDistribution[p]), ...Object.keys(planTierDistribution).filter(p => !PLAN_ORDER.includes(p))];
  if (plans.length === 0) {
    return (
      <div className="admin-info-card">
        <h4>Plan & Tier Distribution</h4>
        <p className="admin-plan-tier-empty">No plan data yet.</p>
      </div>
    );
  }

  const PLAN_ICONS = { max: 'fa-crown', pro: 'fa-star', free: 'fa-user' };

  return (
    <div className="admin-info-card admin-plan-tier-card">
      <h4>Plan & Tier Distribution</h4>
      <div className="admin-plan-tier-blocks">
        {plans.map((planId) => {
          const tiers = planTierDistribution[planId] || {};
          const planTotal = Object.values(tiers).reduce((s, n) => s + (typeof n === 'number' ? n : 0), 0);
          const color = PLAN_COLORS[planId] || '#6b7280';
          const tierIds = getTierOrder(tiers);

          return (
            <div key={planId} className="admin-plan-tier-block">
              <div className="admin-plan-tier-block-header" style={{ borderLeftColor: color }}>
                <i className={`fa-solid ${PLAN_ICONS[planId] || 'fa-layer-group'} admin-plan-tier-block-icon`} style={{ color }}></i>
                <span className="admin-plan-tier-block-name">{planId.charAt(0).toUpperCase() + planId.slice(1)}</span>
                <span className="admin-plan-tier-block-total">{typeof planTotal === 'number' ? planTotal.toLocaleString() : '—'} users</span>
              </div>
              <div className="admin-plan-tier-rows">
                {tierIds.length > 0 ? tierIds.map((tierId) => {
                  const count = tiers[tierId];
                  const n = typeof count === 'number' ? count : 0;
                  return (
                    <div key={tierId} className="admin-plan-tier-row">
                      <span className="admin-plan-tier-row-label">{tierId.charAt(0).toUpperCase() + tierId.slice(1)}</span>
                      <span className="admin-plan-tier-row-value">{n.toLocaleString()}</span>
                    </div>
                  );
                }) : (
                  <div className="admin-plan-tier-row">
                    <span className="admin-plan-tier-row-label">—</span>
                    <span className="admin-plan-tier-row-value">0</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminUsersSection() {
  const utcEndDate = React.useMemo(() => startOfUtcDay(new Date()), []);
  const defaultRange = React.useMemo(() => getRangeFromPreset('last_month', utcEndDate), [utcEndDate]);

  // Switch semantics:
  // - checked=false => exclude the migration-impacted days (Feb 8/21)
  // - checked=true  => include those days
  const [includeSignupMigrationDays, setIncludeSignupMigrationDays] = useState(false);

  const initialSignupOptions = React.useMemo(
    () => ({
      ...defaultRange,
      excludeMigrationDays: !includeSignupMigrationDays,
    }),
    [defaultRange, includeSignupMigrationDays]
  );

  const { manager, isInitialized } = useManager(AdminUsersManager, initialSignupOptions);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingRange, setIsUpdatingRange] = useState(false);
  const [signupGranularity, setSignupGranularity] = useState('day');

  const [rangePreset, setRangePreset] = useState('last_month');
  const [customStart, setCustomStart] = useState(defaultRange.signupsStart);
  const [customEnd, setCustomEnd] = useState(defaultRange.signupsEnd);
  const [effectiveRange, setEffectiveRange] = useState(defaultRange);
  const effectiveSignupOptions = React.useMemo(
    () => ({
      ...effectiveRange,
      excludeMigrationDays: !includeSignupMigrationDays,
    }),
    [effectiveRange, includeSignupMigrationDays]
  );

  const [notificationModal, setNotificationModal] = useState({
    isOpen: false,
    data: null,
    eventType: null
  });

  // Handle notification payloads - check for users page specific payload
  useEffect(() => {
    // Check for payload stored specifically for the users page
    const usersPayloadKey = 'sectionPayload_admin/users';
    const storedPayload = sessionStorage.getItem(usersPayloadKey);

    if (storedPayload) {
      try {
        const payloadData = JSON.parse(storedPayload);
        const payload = payloadData.payload;

        // Check if it's a notification payload (user reg, plan purchase, or product purchase)
        const isHighlightAction = payload?.fromNotification &&
          (payload.action === 'highlight_user' || payload.action === 'highlight_product_purchase');
        if (isHighlightAction) {
          // Determine event type from payload data
          const eventType = payload.productId ? 'product_purchase'
            : payload.planId ? 'plan_purchase'
            : 'user_registration';

          setNotificationModal({
            isOpen: true,
            data: payload,
            eventType
          });

          // Clear the payload after consuming it
          sessionStorage.removeItem(usersPayloadKey);

          logger.log({
            tags: 'admin|users',
            color1: 'green'
          }, 'AdminUsersSection: Consumed notification payload:', payload);
        }
      } catch (error) {
        console.error('AdminUsersSection: Error parsing notification payload:', error);
        sessionStorage.removeItem(usersPayloadKey); // Clean up invalid data
      }
    }
  }, []); // Only run once on mount

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await manager.refreshStats(effectiveSignupOptions);
    } finally {
      setIsRefreshing(false);
    }
  };

  const applyRange = async (nextRange) => {
    setIsUpdatingRange(true);
    try {
      setEffectiveRange(nextRange);
      await manager.refreshStats({ ...nextRange, excludeMigrationDays: !includeSignupMigrationDays });
    } finally {
      setIsUpdatingRange(false);
    }
  };

  const handleIncludeMigrationToggle = async (checked) => {
    setIncludeSignupMigrationDays(checked);
    setIsUpdatingRange(true);
    try {
      await manager.refreshStats({ ...effectiveRange, excludeMigrationDays: !checked });
    } finally {
      setIsUpdatingRange(false);
    }
  };

  const handleRangePresetChange = async (nextPreset) => {
    setRangePreset(nextPreset);

    if (nextPreset === 'custom') {
      // Keep inputs aligned with the currently displayed range.
      setCustomStart(effectiveRange.signupsStart);
      setCustomEnd(effectiveRange.signupsEnd);
      return;
    }

    const nextRange = getRangeFromPreset(nextPreset, utcEndDate);
    setCustomStart(nextRange.signupsStart);
    setCustomEnd(nextRange.signupsEnd);
    await applyRange(nextRange);
  };

  const handleApplyCustomRange = async () => {
    if (!customStart || !customEnd) return;
    const nextRange = { signupsStart: customStart, signupsEnd: customEnd };
    await applyRange(nextRange);
  };

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--admin">
        <div className="loading-container">
          <LoadingPanel
            title="Loading Users"
            subtitle="Loading user statistics and analytics..."
            showAppName={false}
          />
        </div>
      </MainLayout>
    );
  }

  const stats = manager.getUserStats();
  const signupChartData = manager.getSignupSeries(signupGranularity);
  const isChartLoading = isRefreshing || isUpdatingRange;

  return (
    <MainLayout contentClassName="main-content--admin">
      {/* Fixed header with home button */}
      <AdminPageHeader />

      <div className="admin-users-container">
        <div className="admin-section-content">
          {/* Section header */}
          <div className="admin-section-header">
            <h2>Users</h2>
            <p className="admin-section-subtitle">View user statistics and account analytics.</p>
            <RevoltButton
              className="admin-refresh-button"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <i className={`fa-solid fa-rotate-right ${isRefreshing ? 'fa-spin' : ''}`}></i>
              Refresh
            </RevoltButton>
          </div>

          {/* Statistics Cards */}
          <div className="admin-stats-grid">
            <StatCard
              title="Total Users"
              value={stats.totalUsers}
              subtitle="Registered accounts"
              icon="users"
              color="#8b0000"
            />
            <StatCard
              title="Verified Emails"
              value={stats.verifiedEmails}
              subtitle={typeof stats.verifiedEmails === 'number' && typeof stats.totalUsers === 'number' && stats.totalUsers > 0 ?
                `${((stats.verifiedEmails / stats.totalUsers) * 100).toFixed(1)}% verified` : '—'}
              icon="envelope-circle-check"
              color="#10b981"
            />
            <StatCard
              title="Active Users"
              value={stats.activeUsers}
              subtitle="Active this month"
              icon="user-check"
              color="#22c55e"
            />
            <StatCard
              title="Recent Signups"
              value={stats.recentSignups}
              subtitle="Last 30 days"
              icon="user-plus"
              color="#3b82f6"
            />
            <StatCard
              title="Stripe Customers"
              value={stats.stripeCustomers}
              subtitle={typeof stats.stripeCustomers === 'number' && typeof stats.totalUsers === 'number' && stats.totalUsers > 0 ?
                `${((stats.stripeCustomers / stats.totalUsers) * 100).toFixed(1)}% with payment setup` : '—'}
              icon="credit-card"
              color="#6366f1"
            />
            <StatCard
              title="Active Subscriptions"
              value={stats.activeSubscriptions}
              subtitle={typeof stats.activeSubscriptions === 'number' && typeof stats.totalUsers === 'number' && stats.totalUsers > 0 ?
                `${((stats.activeSubscriptions / stats.totalUsers) * 100).toFixed(1)}% paying users` : '—'}
              icon="crown"
              color="#f59e0b"
            />
            <StatCard
              title="Products Purchased"
              value={stats.productsPurchased}
              subtitle="Marketplace purchases"
              icon="bag-shopping"
              color="#10b981"
            />
            <StatCard
              title="Supporters"
              value={stats.supporters}
              subtitle={typeof stats.supporters === 'number' && typeof stats.totalUsers === 'number' && stats.totalUsers > 0 ?
                `${((stats.supporters / stats.totalUsers) * 100).toFixed(1)}% supporters` : '—'}
              icon="heart"
              color="#ec4899"
            />
            <StatCard
              title="Beta Testers"
              value={stats.betaTesters}
              subtitle="Early access users"
              icon="flask"
              color="#f59e0b"
            />
          </div>

          <div className="admin-signups-chart-card stripe-chart-section">
            <div className="admin-signups-chart-header">
              <div className="admin-signups-chart-copy">
                <h4>Signups Over Time</h4>
                <p>Registration trends with daily, weekly, and monthly granularity.</p>
              </div>
              <div className="stripe-chart-toggle-group">
                <span className="stripe-chart-toggle-label">Granularity:</span>
                <div className="stripe-chart-toggle">
                  <button
                    type="button"
                    className={`stripe-toggle-btn ${signupGranularity === 'day' ? 'active' : ''}`}
                    onClick={() => setSignupGranularity('day')}
                  >
                    Day
                  </button>
                  <button
                    type="button"
                    className={`stripe-toggle-btn ${signupGranularity === 'week' ? 'active' : ''}`}
                    onClick={() => setSignupGranularity('week')}
                  >
                    Week
                  </button>
                  <button
                    type="button"
                    className={`stripe-toggle-btn ${signupGranularity === 'month' ? 'active' : ''}`}
                    onClick={() => setSignupGranularity('month')}
                  >
                    Month
                  </button>
                </div>
              </div>

              <div className="stripe-chart-toggle-group admin-signups-range-toggle-group">
                <span className="stripe-chart-toggle-label">Range:</span>
                <div className="admin-signups-range-controls">
                  <DropdownSelect
                    id="admin-signups-range-select"
                    value={rangePreset}
                    options={[
                      { value: 'last_day', label: 'Last day' },
                      { value: 'last_week', label: 'Last week' },
                      { value: 'last_month', label: 'Last month' },
                      { value: 'month_to_date', label: 'Month to date' },
                      { value: 'year_to_date', label: 'Year to date' },
                      { value: 'revolt_start', label: 'Revolt Start' },
                      { value: 'custom', label: 'Custom' },
                    ]}
                    onValueChange={handleRangePresetChange}
                    disabled={isUpdatingRange}
                    className="admin-signups-range-select"
                  />

                  <div className="admin-signups-migration-toggle">
                    <RevoltSwitch
                      checked={includeSignupMigrationDays}
                      onChange={handleIncludeMigrationToggle}
                      label="Include Feb 8/21"
                      disabled={isUpdatingRange}
                      radius={12}
                    />
                  </div>

                  {rangePreset === 'custom' && (
                    <div className="admin-signups-custom-range">
                      <div className="admin-signups-custom-range-row">
                        <label className="admin-signups-custom-range-label">Start</label>
                        <input
                          type="date"
                          className="admin-signups-range-input"
                          value={customStart}
                          onChange={(e) => setCustomStart(e.target.value)}
                          disabled={isUpdatingRange}
                        />
                      </div>
                      <div className="admin-signups-custom-range-row">
                        <label className="admin-signups-custom-range-label">End</label>
                        <input
                          type="date"
                          className="admin-signups-range-input"
                          value={customEnd}
                          onChange={(e) => setCustomEnd(e.target.value)}
                          disabled={isUpdatingRange}
                        />
                      </div>

                      <RevoltButton
                        className="admin-signups-range-apply-button"
                        onClick={handleApplyCustomRange}
                        disabled={isUpdatingRange}
                      >
                        {isUpdatingRange ? 'Applying...' : 'Apply'}
                      </RevoltButton>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <SignupsOverTimeChart
              chartData={signupChartData}
              granularity={signupGranularity}
              isLoading={isChartLoading}
            />
          </div>

          {/* Plan & Tier Distribution + Apps & Storage Metrics */}
          <div className="admin-users-info">
            <PlanTierDistribution planTierDistribution={stats.planTierDistribution} />
            <div className="admin-info-card admin-apps-storage-card">
              <h4>Apps & Storage</h4>
              <div className="admin-metrics-blocks">
                <div className="admin-metrics-block">
                  <div className="admin-metrics-block-header">
                    <i className="fa-solid fa-cube admin-metrics-block-icon"></i>
                    <span>Apps</span>
                  </div>
                  <div className="admin-metrics-rows">
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Total Apps</span>
                      <span className="admin-metrics-row-value">{typeof stats.totalApps === 'number' ? stats.totalApps.toLocaleString() : '—'}</span>
                    </div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Paid Users</span>
                      <span className="admin-metrics-row-value">{typeof stats.appsByPlanType?.paid === 'number' ? stats.appsByPlanType.paid.toLocaleString() : '—'}</span>
                    </div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Free Users</span>
                      <span className="admin-metrics-row-value">{typeof stats.appsByPlanType?.free === 'number' ? stats.appsByPlanType.free.toLocaleString() : '—'}</span>
                    </div>
                    <div className="admin-metrics-divider"></div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Avg per Paid User</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.paidUsersWithApps === 'number' && typeof stats.totalAppsByPaidUsers === 'number' && stats.paidUsersWithApps > 0
                          ? (stats.totalAppsByPaidUsers / stats.paidUsersWithApps).toFixed(1)
                          : '—'
                      }</span>
                    </div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Avg per Free User</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.freeUsersWithApps === 'number' && typeof stats.appsByPlanType?.free === 'number' && stats.freeUsersWithApps > 0
                          ? (stats.appsByPlanType.free / stats.freeUsersWithApps).toFixed(1)
                          : '—'
                      }</span>
                    </div>
                  </div>
                </div>
                <div className="admin-metrics-block">
                  <div className="admin-metrics-block-header">
                    <i className="fa-solid fa-database admin-metrics-block-icon"></i>
                    <span>Storage</span>
                  </div>
                  <div className="admin-metrics-rows">
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Total</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.totalStorageBytes === 'number'
                          ? (stats.totalStorageBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
                          : '—'
                      }</span>
                    </div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Paid Users</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.paidUsersStorageBytes === 'number'
                          ? (stats.paidUsersStorageBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
                          : '—'
                      }</span>
                    </div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Free Users</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.freeUsersStorageBytes === 'number'
                          ? (stats.freeUsersStorageBytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB'
                          : '—'
                      }</span>
                    </div>
                    <div className="admin-metrics-divider"></div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Avg App Size (All)</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.totalStorageBytes === 'number' && typeof stats.totalApps === 'number' && stats.totalApps > 0
                          ? (stats.totalStorageBytes / stats.totalApps / (1024 * 1024)).toFixed(1) + ' MB'
                          : '—'
                      }</span>
                    </div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Avg App Size (Paid)</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.paidUsersStorageBytes === 'number' && typeof stats.appsByPlanType?.paid === 'number' && stats.appsByPlanType.paid > 0
                          ? (stats.paidUsersStorageBytes / stats.appsByPlanType.paid / (1024 * 1024)).toFixed(1) + ' MB'
                          : '—'
                      }</span>
                    </div>
                    <div className="admin-metrics-row">
                      <span className="admin-metrics-row-label">Avg App Size (Free)</span>
                      <span className="admin-metrics-row-value">{
                        typeof stats.freeUsersStorageBytes === 'number' && typeof stats.appsByPlanType?.free === 'number' && stats.appsByPlanType.free > 0
                          ? (stats.freeUsersStorageBytes / stats.appsByPlanType.free / (1024 * 1024)).toFixed(1) + ' MB'
                          : '—'
                      }</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <NotificationModal
        isOpen={notificationModal.isOpen}
        onClose={() => setNotificationModal({ isOpen: false, data: null, eventType: null })}
        notificationData={notificationModal.data}
        eventType={notificationModal.eventType}
      />
    </MainLayout>
  );
}